import { Component, Input, NgZone, OnDestroy } from "@angular/core";
import moment from 'moment';
import { Subscription } from "rxjs";
import { ImpersonationService } from "../../../../auth/apigee/impersonation.service";
import { LoggerService } from "../../../../common/logging/logger.service";
import { ModalDialogComponent } from "../../../../common/modal/modalDialog.component";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import { OffersInNegotiationDetail, OffersInNegotiationService, OffersInNegotiationServiceData } from "../../services/offersInNegotiationKpi/offersInNegotiation.service";
import { MultiProgressDataset, MultiProgressItem } from "../../../../dashboardCommon/components/cardContents/multiProgress.component";
import { ProgressBarStyle } from "../../../../dashboardCommon/components/cardContents/progressBar.component";
import { StackedProgressBarItem } from "../../../../dashboardCommon/components/cardContents/stackedProgressBar.component";
import { ColumnDefinition, DateColumnDefinition, TextColumnDefinition } from "../../../../dashboardCommon/components/cardDetails/datatable.component";
import { SingleSummaryDetailKpiServiceHost } from "../../../../dashboardCommon/components/kpiServiceHostComponent";
import { OffersInNegotiationData } from "./offersInNegotiationKpiSeviceHost.component";

export class OffersInNegotiationExtendedItemData {
    public segment: number;
    public value: number;

    constructor(segment: number, value: number) {
        this.segment = segment;
        this.value = value;
    }
}

export class OffersInNegotiationExtendedData extends OffersInNegotiationData {
    public lessThanThreeMonths: OffersInNegotiationExtendedItemData[];
    public lessThanThreeMonthsCount: number;
    public threeMonthsAndUp: OffersInNegotiationExtendedItemData[];
    public threeMonthsAndUpCount: number;
    public multiProgressData: MultiProgressDataset;

    constructor(offerCount: number, lessThanThreeMonths: OffersInNegotiationExtendedItemData[], threeMonthsAndUp: OffersInNegotiationExtendedItemData[]) {
        super(offerCount);
        this.lessThanThreeMonths = lessThanThreeMonths || [];
        this.lessThanThreeMonthsCount = this.lessThanThreeMonths.reduce<number>((acc, x) => acc + x.value, 0);
        this.threeMonthsAndUp = threeMonthsAndUp || [];
        this.threeMonthsAndUpCount = this.threeMonthsAndUp.reduce<number>((acc, x) => acc + x.value, 0);

        let groups = [];

        if (this.lessThanThreeMonthsCount > 0) {
            let group0 = new MultiProgressItem();
            group0.mainLegend = "1-2 meses";
            group0.min = 0;
            group0.max = this.lessThanThreeMonthsCount;
            group0.data = this.lessThanThreeMonths.map(x => {
                let item = new StackedProgressBarItem();
                item.text = x.value.toString();
                item.value = x.value;
                item.legend = "S" + x.segment;
                switch (x.segment) {
                    case 1: item.style = ProgressBarStyle.Warn; break;
                    case 2: item.style = ProgressBarStyle.Warn2; break;
                    case 3: item.style = ProgressBarStyle.Warn3; break;
                    case 4: item.style = ProgressBarStyle.Warn4; break;
                }
                return item;
            });

            groups.push(group0);
        }

        if (this.threeMonthsAndUpCount > 0) {
            let group1 = new MultiProgressItem();
            group1.mainLegend = "+2 meses";
            group1.min = 0;
            group1.max = this.threeMonthsAndUpCount;
            group1.data = this.threeMonthsAndUp.map(x => {
                let item = new StackedProgressBarItem();
                item.text = x.value.toString();
                item.value = x.value;
                item.legend = "S" + x.segment;
                switch (x.segment) {
                    case 1: item.style = ProgressBarStyle.Alert; break;
                    case 2: item.style = ProgressBarStyle.Alert2; break;
                    case 3: item.style = ProgressBarStyle.Alert3; break;
                    case 4: item.style = ProgressBarStyle.Alert4; break;
                }
                return item;
            });

            groups.push(group1);
        }

        this.multiProgressData = new MultiProgressDataset(groups);
    }
}

export class OffersInNegotiationExtendedDetailData {
    public columnDefinitions: ColumnDefinition[];
    public tabledata: any[][];

    constructor(columnDefinitions: ColumnDefinition[], tabledata: any[][]) {
        this.columnDefinitions = columnDefinitions;
        this.tabledata = tabledata;
    }
}

@Component({
    selector: 'offers-in-negotiation-extended-kpi-service-host',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class OffersInNegotiationExtendedKpiServiceHostComponent extends SingleSummaryDetailKpiServiceHost<OffersInNegotiationService, OffersInNegotiationServiceData, OffersInNegotiationExtendedData, OffersInNegotiationExtendedDetailData>
    implements OnDestroy {
    public getClassName() { return "OffersInNegotiationExtendedKpiServiceHostComponent"; }

    private raasSubscription: Subscription;
    private officeSelectorSubscription: Subscription;
    @Input() public detailDialog: ModalDialogComponent;
    @Input() public messageDialog: ModalDialogComponent;

    constructor(zone: NgZone, logger: LoggerService, private impersonationService: ImpersonationService, private officeSelectorService: OfficeSelectorService,
        summaryDetailService: OffersInNegotiationService) {
        super(zone, logger, summaryDetailService);
        this.raasSubscription = this.impersonationService.impersonationChanged.subscribe(() => { this.resetSummary(); });
        this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => { this.resetSummary(); });
    }

    protected processSummaryData(data: OffersInNegotiationServiceData, updateRequested: Boolean): OffersInNegotiationExtendedData {
        if (data.consultantEntry) {
            let now = moment();
            let offersByDate = data.consultantEntry.offersInNegotiation.reduce<{ lessThanThreeMonths: OffersInNegotiationDetail[]; threeMonthsAndUp: OffersInNegotiationDetail[]; }>((acc, offer) => {

                if (moment.duration(now.diff(moment(offer.date))).months() <2) {
                    acc.lessThanThreeMonths.push(offer);

                } else {

                    acc.threeMonthsAndUp.push(offer);
                }
                return acc;
            }, { lessThanThreeMonths: [], threeMonthsAndUp: [] });

            let accInit = {};
            accInit[1] = { segment: 1, count: 0 };
            accInit[2] = { segment: 2, count: 0 };
            accInit[3] = { segment: 3, count: 0 };
            accInit[4] = { segment: 4, count: 0 };

            let lessThanThreeMonthsSegmented = offersByDate.lessThanThreeMonths.reduce<{ [key: number]: OffersInNegotiationExtendedItemData; }>((acc, offer) => {
                if (acc[offer.segment]) {
                    let accItem = acc[offer.segment];
                    accItem.value++;
                } else {
                    let accItem = new OffersInNegotiationExtendedItemData(Number.parseInt(offer.segment), 1);
                    acc[Number.parseInt(offer.segment)] = accItem;
                }
                return acc;
            }, accInit);

            let lessThanThreeMonthsProcessed: OffersInNegotiationExtendedItemData[] = [];
            for (let key in lessThanThreeMonthsSegmented) {
                lessThanThreeMonthsProcessed.push(lessThanThreeMonthsSegmented[key]);
            }
            lessThanThreeMonthsProcessed = lessThanThreeMonthsProcessed.sort((a, b) => a.segment - b.segment);

            accInit = {};
            accInit[1] = { segment: 1, count: 0 };
            accInit[2] = { segment: 2, count: 0 };
            accInit[3] = { segment: 3, count: 0 };
            accInit[4] = { segment: 4, count: 0 };

            let threeMonthsAndUpSegmented = offersByDate.threeMonthsAndUp.reduce<{ [key: number]: OffersInNegotiationExtendedItemData; }>((acc, offer) => {
                if (acc[offer.segment]) {
                    let accItem = acc[offer.segment];
                    accItem.value++;
                } else {
                    let accItem = new OffersInNegotiationExtendedItemData(Number.parseInt(offer.segment), 1);
                    acc[Number.parseInt(offer.segment)] = accItem;
                }
                return acc;
            }, accInit);

            let threeMonthsAndUpProcessed: OffersInNegotiationExtendedItemData[] = [];
            for (let key in threeMonthsAndUpSegmented) {
                threeMonthsAndUpProcessed.push(threeMonthsAndUpSegmented[key]);
            }
            threeMonthsAndUpProcessed = threeMonthsAndUpProcessed.sort((a, b) => a.segment - b.segment);

            let diff = moment.duration(now.diff(moment(data.consultantEntry.offersInNegotiationUpdateTimestamp)));
            if (diff.hours() >= 4) {
                return new OffersInNegotiationExtendedData(data.consultantEntry.offersInNegotiationCount, lessThanThreeMonthsProcessed, threeMonthsAndUpProcessed);
            }
            else {
                if (this.summaryData) {
                    if (updateRequested) {
                        this.messageDialog.open();
                        return new OffersInNegotiationExtendedData(data.consultantEntry.offersInNegotiationCount, lessThanThreeMonthsProcessed, threeMonthsAndUpProcessed);
                    }
                    else {
                        return new OffersInNegotiationExtendedData(data.consultantEntry.offersInNegotiationCount, lessThanThreeMonthsProcessed, threeMonthsAndUpProcessed);
                    }
                }
                else {
                    return new OffersInNegotiationExtendedData(data.consultantEntry.offersInNegotiationCount, lessThanThreeMonthsProcessed, threeMonthsAndUpProcessed);
                }
            }
        } else {
            return this.getDefaultSummaryData();
        }
    }

    protected getDefaultSummaryData(): OffersInNegotiationExtendedData {
        let data = new OffersInNegotiationExtendedData(0, [], []);
        return data;
    }

    protected extractUpdateTime(data: OffersInNegotiationServiceData): Date {
        if (data.consultantEntry) {
            return data.consultantEntry.offersInNegotiationUpdateTimestamp;
        } else {
            return new Date();
        }
    }


    protected processDetailData(data: OffersInNegotiationServiceData): OffersInNegotiationExtendedDetailData {
        if (data.entries) {
            let columnDefinitions = [
                new TextColumnDefinition("Razón social"),
                new TextColumnDefinition("Segmento"),
                new DateColumnDefinition("Fecha", (data) => moment(data).format("DD/MM/YYYY")),
                new TextColumnDefinition("Potencial"),
                new TextColumnDefinition("Margen")
            ];

            let tabledata = [];
            for (let entry of data.entries) {
                if (entry.nif.toUpperCase() == this.impersonationService.getNifSafe().toUpperCase()) {
                    for (let offer of entry.offersInNegotiation || []) {
                        tabledata.push([
                            offer.trading_name,
                            "S" + offer.segment,
                            offer.date,
                            offer.potential,
                            offer.min_margin
                        ]);
                    }
                }
            }

            return new OffersInNegotiationExtendedDetailData(columnDefinitions, tabledata);
        } else return this.getDefaultDetailData();
    }

    protected getDefaultDetailData(): OffersInNegotiationExtendedDetailData {
        let columnDefinitions = [
            new TextColumnDefinition("Razón social"),
            new TextColumnDefinition("Segmento"),
            new DateColumnDefinition("Fecha", (data) => moment(data).format("DD/MM/YYYY")),
            new TextColumnDefinition("Potencial"),
            new TextColumnDefinition("Margen")
        ];

        return new OffersInNegotiationExtendedDetailData(columnDefinitions, []);
    }

    protected performDetailAction(): void {
        if (this.detailDialog) this.detailDialog.open();
    }

    public ngOnDestroy(): void {
        if (this.raasSubscription) this.raasSubscription.unsubscribe();
        if (this.officeSelectorSubscription) this.officeSelectorSubscription.unsubscribe();
    }
}
